'use client'

import { LineFileText } from '@qr-front/icons/line-file-text'
import { LineLocationPin } from '@qr-front/icons/line-location-pin'
import { LinePhone } from '@qr-front/icons/line-phone'
import { LineStar } from '@qr-front/icons/line-star'
import { LineThumbsUp } from '@qr-front/icons/line-thumbs-up'
import { LineTime } from '@qr-front/icons/line-time'
import { cn } from '@qr-front/ui/utils'
import * as Accordion from '@radix-ui/react-accordion'
import { socialLinkListAtom } from 'features/generator/contentFormSections'
import {
	contactInfoEmailListAtom,
	contactInfoPhoneListAtom,
	contactInfoWebsiteAtom,
} from 'features/generator/contentFormSections/CommonSections/ContactSection'
import { contactInfoFullNameAtom } from 'features/generator/contentFormSections/ContactInfoForm/ContactInfoForm'
import { facilitiesAtom } from 'features/generator/contentFormSections/FacilitiesForm/FacilitiesForm'
import { addressUrlAtom } from 'features/generator/contentFormSections/LocationForm/addressAtoms'
import {
	openingHoursStateAtom,
	sharedOpeningHoursStateAtom,
	weekDays,
	weekDaysAreSharedAtom,
} from 'features/generator/contentFormSections/OpeningHoursForm/openingHoursState'
import { selectedTabAtom } from 'features/generator/contentFormSections/OpeningHoursForm/TabNavigation'
import { summaryAtom } from 'features/generator/contentFormSections/SummaryForm/SummarySection'
import { QrScreen, TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { ReactElement, useEffect, useMemo, useState } from 'react'

import { TSvgIcon } from '@/types'

import { useFullLocation } from '../../../contentFormSections/LocationForm'
import { AccordionItem } from './BusinessPageContentSections/AccordionItem'
import { ContactSection } from './BusinessPageContentSections/ContactSection'
import { FacilitySection } from './BusinessPageContentSections/FacilitySection'
import { LocationSection } from './BusinessPageContentSections/LocationSection'
import { OpenHoursSection } from './BusinessPageContentSections/OpenHoursSection'
import { SocialSection } from './BusinessPageContentSections/SocialSection'
import { SummarySection } from './BusinessPageContentSections/SummarySection'
import { BusinessPageHeader } from './BusinessPageHeader'
import { IsPreviewModeProvider } from './BusinessPageScreenContexts'
import { businessPagePrimaryColorAtom } from './BusinessPageScreenState'

export const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const t = useTranslations()

	const fullLocation = useFullLocation()

	const selectedOpeningHoursTab = useAtomValue(selectedTabAtom)
	const openingHours = useAtomValue(openingHoursStateAtom)
	const sharedOpeningHours = useAtomValue(sharedOpeningHoursStateAtom)
	const weekDaysAreShared = useAtomValue(weekDaysAreSharedAtom)
	const addressUrl = useAtomValue(addressUrlAtom)
	const fullName = useAtomValue(contactInfoFullNameAtom)
	const phoneList = useAtomValue(contactInfoPhoneListAtom)
	const website = useAtomValue(contactInfoWebsiteAtom)
	const emailList = useAtomValue(contactInfoEmailListAtom)

	const hasOpenHoursItems = useMemo(() => {
		if (demoMode) return true

		if (selectedOpeningHoursTab === 'open-24h') return true

		const activeDaysWithContent = weekDays
			.filter((day, index) => {
				if (weekDaysAreShared && index > 4) return false
				const dayData = weekDaysAreShared
					? sharedOpeningHours[day]
					: openingHours[day]
				return dayData?.isActive
			})
			.filter((day) => {
				const source = weekDaysAreShared
					? sharedOpeningHours[day]
					: openingHours[day]
				return source?.dateRanges && source.dateRanges.length > 0
			})

		return activeDaysWithContent.length > 0
	}, [
		openingHours,
		sharedOpeningHours,
		selectedOpeningHoursTab,
		weekDaysAreShared,
		demoMode,
	])

	const hasLocationItems = addressUrl || fullLocation || demoMode

	const hasFacilitiyItems = useAtomValue(facilitiesAtom)?.length || demoMode

	const hasContactItems =
		fullName ||
		phoneList.filter((phone) => phone.value.length).length ||
		website ||
		emailList.filter((email) => email.value.length).length ||
		demoMode

	const hasSocialLinks = useAtomValue(socialLinkListAtom).length || demoMode

	const hasSummary = useAtomValue(summaryAtom)?.length || demoMode

	const openHoursSection = useMemo(
		() =>
			hasOpenHoursItems
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__open_hours__title',
						),
						Icon: LineTime,
						content: <OpenHoursSection />,
					}
				: null,
		[hasOpenHoursItems, t],
	)

	const locationSection = useMemo(
		() =>
			hasLocationItems
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__location__title',
						),
						Icon: LineLocationPin,
						content: <LocationSection />,
					}
				: null,
		[hasLocationItems, t],
	)

	const facilitySection = useMemo(
		() =>
			hasFacilitiyItems
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__facilities__title',
						),
						Icon: LineStar,
						content: <FacilitySection />,
					}
				: null,
		[hasFacilitiyItems, t],
	)

	const summarySection = useMemo(
		() =>
			hasSummary
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__summary__title',
						),
						Icon: LineFileText,
						content: <SummarySection />,
					}
				: null,
		[hasSummary, t],
	)

	const contactSection = useMemo(
		() =>
			hasContactItems
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__contact__title',
						),
						Icon: LinePhone,
						content: <ContactSection />,
					}
				: null,
		[hasContactItems, t],
	)

	const socialSection = useMemo(
		() =>
			hasSocialLinks
				? {
						title: t(
							'generator__qr_content_screen__business_page_type__sections__social__title',
						),
						Icon: LineThumbsUp,
						content: <SocialSection />,
					}
				: null,
		[hasSocialLinks, t],
	)

	const sectionList = useMemo(
		() =>
			[
				openHoursSection,
				locationSection,
				facilitySection,
				summarySection,
				contactSection,
				socialSection,
			] as const satisfies ({
				title: string
				Icon: TSvgIcon
				content: ReactElement
			} | null)[],
		[
			openHoursSection,
			locationSection,
			facilitySection,
			summarySection,
			contactSection,
			socialSection,
		],
	)

	const [openSectionList, setOpenSectionList] = useState<string[]>([])

	useEffect(() => {
		if (previewMode && !demoMode) {
			setOpenSectionList(
				sectionList
					.filter((section) => section !== null)
					.map(({ title }) => title),
			)
		}
	}, [demoMode, previewMode, sectionList])
	return (
		<IsPreviewModeProvider value={previewMode}>
			<div className={cn('relative', demoMode && 'pointer-events-none')}>
				<BusinessPageHeader
					previewMode={previewMode}
					demoMode={demoMode}
				/>
				<Accordion.Root
					type="multiple"
					value={openSectionList}
					onValueChange={setOpenSectionList}
					className="mx-auto -mt-6 mb-6 flex max-w-qr-screen-constrained flex-col gap-2 px-5"
				>
					{sectionList
						.filter((value) => value !== null)
						.map(({ title, content, Icon }) => (
							<AccordionItem
								key={title}
								title={title}
								Icon={Icon}
								content={content}
								isOpen={openSectionList.includes(title)}
							/>
						))}
				</Accordion.Root>
			</div>
		</IsPreviewModeProvider>
	)
}

export const BusinessPageScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(businessPagePrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
